import { IOnboardingInfoModalContent } from '@/types/shared'
import React, { FC, useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'

interface OnboardingTooltipProps {
  targetElementId: string | null
  isOpen: boolean
  content: IOnboardingInfoModalContent | null
  onNext?: () => void
  onSkip?: () => void
  onClose?: () => void
  isLoading?: boolean
  zIndex?: number
  showBackdrop?: boolean
  overlayPadding?: number
  preferredTooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
}

export const OnboardingTooltip: FC<OnboardingTooltipProps> = ({
  targetElementId,
  isOpen,
  content,
  onNext,
  onSkip,
  onClose,
  isLoading = false,
  zIndex = 10000,
  showBackdrop = false,
  overlayPadding = 8,
  preferredTooltipPlacement,
}) => {
  // Debug: uncomment for debugging
  // console.log('[ONBOARDING] OnboardingTooltip props:', {
  //   targetElementId,
  //   isOpen,
  //   hasContent: !!content,
  //   content,
  //   zIndex,
  // })

  const [elementRect, setElementRect] = useState<DOMRect | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number
    left: number
    placement: 'top' | 'bottom' | 'left' | 'right'
  } | null>(null)

  const updateElementRect = useCallback(() => {
    // Debug: updateElementRect
    // console.log('[ONBOARDING] updateElementRect called with targetElementId:', targetElementId)

    if (!targetElementId) {
      setElementRect(null)
      return
    }

    const element = document.getElementById(targetElementId)

    if (element) {
      let rect = element.getBoundingClientRect()

      // For lesson elements, use stable positioning to prevent hover jitter
      if (targetElementId?.includes('onboarding-first-lesson')) {
        const button = element.querySelector('button')
        if (button) {
          const buttonRect = button.getBoundingClientRect()
          // Use stable rect for lesson elements
          rect = {
            top: rect.top,
            bottom: rect.bottom,
            height: rect.height,
            left: buttonRect.left,
            right: buttonRect.left + Math.min(buttonRect.width, 300),
            width: Math.min(buttonRect.width, 300),
            x: buttonRect.left,
            y: rect.top,
            toJSON: () => ({}),
          } as DOMRect
        }
      }

      setElementRect(rect)

      // Calculate optimal tooltip position
      const tooltipWidth = 300
      const tooltipMinHeight = 120
      const spacing = 12

      let placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom'
      let top = rect.bottom + spacing
      let left = rect.left + rect.width / 2 - tooltipWidth / 2

      // Special positioning for lesson explorer step - show tooltip above to avoid covering the tree
      if (targetElementId === 'lesson-explorer-themes') {
        placement = 'top'
        top = rect.top - tooltipMinHeight - 35 // Extra spacing to avoid covering content
        left = rect.left + rect.width / 2 - tooltipWidth / 2

        // Ensure it doesn't go off-screen horizontally
        if (left < spacing) {
          left = spacing
        } else if (left + tooltipWidth > window.innerWidth - spacing) {
          left = window.innerWidth - tooltipWidth - spacing
        }

        // If it goes off-screen vertically, fall back to default logic
        if (top >= spacing) {
          setTooltipPosition({ top, left, placement })
          return
        }
      }

      // Check if tooltip fits below the element
      if (top + tooltipMinHeight > window.innerHeight) {
        // Try above
        placement = 'top'
        top = rect.top - tooltipMinHeight - spacing

        if (top < 0) {
          // Try to the right
          placement = 'right'
          top = rect.top + rect.height / 2 - tooltipMinHeight / 2
          left = rect.right + spacing

          if (left + tooltipWidth > window.innerWidth) {
            // Try to the left
            placement = 'left'
            left = rect.left - tooltipWidth - spacing
          }
        }
      }

      // Ensure tooltip doesn't go off screen horizontally
      if (placement === 'top' || placement === 'bottom') {
        if (left < spacing) {
          left = spacing
        } else if (left + tooltipWidth > window.innerWidth - spacing) {
          left = window.innerWidth - tooltipWidth - spacing
        }
      }

      // Ensure tooltip doesn't go off screen vertically
      if (placement === 'left' || placement === 'right') {
        if (top < spacing) {
          top = spacing
        } else if (top + tooltipMinHeight > window.innerHeight - spacing) {
          top = window.innerHeight - tooltipMinHeight - spacing
        }
      }

      // handle case when element is small and placement could not be changed
      if (
        placement === 'top' &&
        (targetElementId === 'dp-tutorial-maths-kb-toggle' ||
          targetElementId === 'dp-tutorial-input-mode-switcher' ||
          targetElementId === 'qr-code-popover' ||
          targetElementId === 'local-image-upload')
      ) {
        top -= overlayPadding * 2

        if (
          targetElementId === 'qr-code-popover' ||
          targetElementId === 'local-image-upload'
        ) {
          top -= overlayPadding * 2
        }
      }

      setTooltipPosition({ top, left, placement })
    } else {
      // Debug: Element not found
      // console.warn(`[ONBOARDING] Element not found: ${targetElementId}`)

      setElementRect(null)
      setTooltipPosition(null)
    }
  }, [overlayPadding, targetElementId])

  useEffect(() => {
    if (!isOpen) {
      setElementRect(null)
      setTooltipPosition(null)
      return
    }

    updateElementRect()

    // Update on scroll or resize
    const handleUpdate = () => updateElementRect()
    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    // Use MutationObserver to detect DOM changes
    const observer = new MutationObserver(handleUpdate)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
      observer.disconnect()
    }
  }, [isOpen, updateElementRect])

  // For steps without targetElementId (like overview), show tooltip in center
  const shouldShowCentered = isOpen && content && !targetElementId
  const shouldShowPositioned =
    isOpen && content && elementRect && tooltipPosition

  if (!isOpen || !content) {
    return null
  }

  if (!shouldShowCentered && !shouldShowPositioned) {
    return null
  }

  // Debug: mode
  // console.log('[ONBOARDING] Tooltip rendering:', {
  //   mode: shouldShowCentered ? 'centered' : 'positioned',
  //   targetElementId,
  // })

  // Determine tooltip style and arrow visibility
  let tooltipStyle: React.CSSProperties
  let showArrow = false

  if (shouldShowCentered) {
    // Center the tooltip on screen, slightly above center
    tooltipStyle = {
      top: '40%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex,
      minWidth: '280px',
      maxWidth: '320px',
    }
    showArrow = false
  } else if (shouldShowPositioned && tooltipPosition) {
    // Position relative to target element
    tooltipStyle = {
      top: tooltipPosition.top,
      left: tooltipPosition.left,
      zIndex,
      minWidth: '280px',
      maxWidth: '320px',
    }
    showArrow = true
  } else {
    // This shouldn't happen given our checks above
    return null
  }

  const tooltipContent = (
    <>
      {/* Backdrop only for Welcome modal */}
      {showBackdrop && (
        <div
          className="fixed inset-0 bg-black bg-opacity-10 backdrop-blur-sm"
          style={{ zIndex: zIndex - 1 }}
        />
      )}

      <div
        className="fixed bg-white rounded-v8 border border-v8-line shadow-v8-card border border-v8-line p-4 max-w-sm"
        style={tooltipStyle}
      >
        {/* Arrow - only show for positioned tooltips */}
        {showArrow && tooltipPosition && (
          <div
            className={`absolute w-3 h-3 bg-white border transform rotate-45 ${
              tooltipPosition.placement === 'bottom'
                ? '-top-1.5 left-1/2 -translate-x-1/2 border-r-0 border-b-0'
                : tooltipPosition.placement === 'top'
                ? '-bottom-1.5 left-1/2 -translate-x-1/2 border-l-0 border-t-0'
                : tooltipPosition.placement === 'right'
                ? '-left-1.5 top-1/2 -translate-y-1/2 border-r-0 border-b-0'
                : '-right-1.5 top-1/2 -translate-y-1/2 border-l-0 border-t-0'
            }`}
          />
        )}

        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-v8-mut hover:text-v8-mut transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Content */}
        <div className="pr-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-v8-blue/10 flex items-center justify-center">
              <svg
                className="w-4 h-4 text-v8-blue"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-v8-ink mb-1">
                {content.title}
              </h3>
              <p className="text-xs text-v8-mut leading-relaxed">
                {content.description}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          {(onNext || onSkip) && (
            <div className="flex items-center justify-between">
              {/* Left side: Skip button or empty div */}
              <div>
                {onSkip && (
                  <button
                    onClick={onSkip}
                    disabled={isLoading}
                    className="text-xs text-v8-mut hover:text-v8-ink disabled:opacity-50 transition-colors"
                  >
                    {content.skipButtonText || 'Skip Tutorial'}
                  </button>
                )}
              </div>

              {/* Right side: Next button */}
              {onNext && (
                <button
                  onClick={onNext}
                  disabled={isLoading}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-v8-blue border border-transparent rounded hover:bg-v8-violet focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-v8-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-1 h-3 w-3 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Loading...
                    </div>
                  ) : (
                    content.nextButtonText || 'Next'
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )

  return createPortal(tooltipContent, document.body)
}
